import { useMutation } from '@apollo/client'
import {
  deserializePin,
  getCurrentEncryptionVersion,
  parsePinToParts,
  savePersonalKeys,
} from '@faceup/crypto'
import { useContext, useEffect, useRef, useState } from 'react'
import { graphql } from '../../../__generated__'
import { LayoutContext } from '../../../Contexts/LayoutContext'
import { ReportFormContext } from '../../../Contexts/ReportFormContext'
import { UserContext } from '../../../Contexts/UserContext'
import { usePreLogin } from '../../../hooks/usePreLogin'
import useAuth from '../../../utils/auth'
import { isAkutanPreview } from '../../../utils/urls'
import useReportAuth from '../../../utils/useReportAuth'

const mutations = {
  VictimLogin: graphql(`
    mutation AutoVictimLoginMutation($input: VictimLoginInput!) {
      victimLogin(input: $input) {
        token
        publicKey
        privateKey
        nonce
      }
    }
  `),
}

export const useAutoVictimLogin = () => {
  const { victimPin } = useContext(ReportFormContext)
  const { reportSource } = useContext(UserContext)
  const { client } = useContext(LayoutContext)
  const { setJwt } = useAuth()
  const { logout } = useReportAuth()
  const { performPreLogin } = usePreLogin()
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [loading, setLoading] = useState(false)
  const loginAttempted = useRef(false)

  const [loginReport] = useMutation(mutations.VictimLogin)

  // Auto-login on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies(performPreLogin): loginAttempted ref guards against re-execution
  // biome-ignore lint/correctness/useExhaustiveDependencies(loginReport): loginAttempted ref guards against re-execution
  // biome-ignore lint/correctness/useExhaustiveDependencies(setJwt): loginAttempted ref guards against re-execution
  // biome-ignore lint/correctness/useExhaustiveDependencies(client): loginAttempted ref guards against re-execution
  useEffect(() => {
    if (isAkutanPreview() || !victimPin || !reportSource || loginAttempted.current) {
      return
    }
    loginAttempted.current = true

    const login = async () => {
      setLoading(true)
      try {
        const { passwordKey, passwordKeyPrehash } = await performPreLogin(victimPin)
        const pin = deserializePin(victimPin)
        const { identity, version } = parsePinToParts(pin)

        const response = await loginReport({
          variables: {
            input: {
              identity,
              passwordPrehash: passwordKeyPrehash,
              version,
              reportPassword: reportSource.password,
              rememberMe: false,
              organizationalUnitId: reportSource.institutionId ?? '',
            },
          },
        })

        const loginData = response.data?.victimLogin
        if (!loginData) {
          return
        }
        await setJwt(loginData.token ?? '')

        if (passwordKey && version === getCurrentEncryptionVersion()) {
          await savePersonalKeys({
            publicKey: loginData.publicKey ?? '',
            privateKey: loginData.privateKey ?? '',
            nonce: loginData.nonce ?? '',
            passwordKey,
            rememberMe: client === 'app',
            version,
          })
        }

        setIsLoggedIn(true)
      } catch (error) {
        console.error('Auto-login failed:', error)
      } finally {
        setLoading(false)
      }
    }

    void login()
  }, [victimPin, reportSource])

  // Cleanup: logout when leaving the ReportSent page
  // biome-ignore lint/correctness/useExhaustiveDependencies(logout): logout reference changes on each render
  useEffect(() => {
    return () => {
      void logout()
    }
  }, [])

  return { isLoggedIn, loading }
}
